import { createContext, ReactNode, useCallback, useContext, useEffect, useId, useMemo, useState } from "react"

import PostLoginHeader, { PostLoginHeaderProps } from "@/components/shared/PostLoginHeader"

export interface DashboardHeaderConfig extends PostLoginHeaderProps {
  containerClassName?: string
}

interface DashboardHeaderActions {
  setHeaderConfig: (ownerId: string, config: DashboardHeaderConfig) => void
  clearHeaderConfig: (ownerId: string) => void
}

// Stable context: setter functions never change, so subscribers won't re-render
// when the displayed header config changes.
const DashboardHeaderActionsContext = createContext<DashboardHeaderActions | null>(null)

// Value context: only consumed by the renderer, not by setter components.
const DashboardHeaderValueContext = createContext<DashboardHeaderConfig | undefined>(undefined)

export function DashboardHeaderProvider({ children }: { children: ReactNode }) {
  const [headerState, setHeaderState] = useState<{ ownerId: string; config: DashboardHeaderConfig }>()

  const setHeaderConfig = useCallback((ownerId: string, config: DashboardHeaderConfig) => {
    setHeaderState((current) => {
      if (current?.ownerId === ownerId && current?.config === config) return current
      console.debug("[PostLoginHeader] setHeaderConfig", { ownerId, config })
      return { ownerId, config }
    })
  }, [])

  const clearHeaderConfig = useCallback((ownerId: string) => {
    setHeaderState((currentState) => {
      if (!currentState || currentState.ownerId !== ownerId) return currentState
      console.debug("[PostLoginHeader] clearHeaderConfig", { ownerId })
      return undefined
    })
  }, [])

  // Memoize so this object reference is stable and never triggers re-renders in
  // setter components. setHeaderConfig/clearHeaderConfig are already stable
  // (useCallback with [] deps), so this memo value is created exactly once.
  const actionsValue = useMemo(() => ({ setHeaderConfig, clearHeaderConfig }), [setHeaderConfig, clearHeaderConfig])

  return (
    <DashboardHeaderActionsContext.Provider value={actionsValue}>
      <DashboardHeaderValueContext.Provider value={headerState?.config}>
        {children}
      </DashboardHeaderValueContext.Provider>
    </DashboardHeaderActionsContext.Provider>
  )
}

export function DashboardHeaderRenderer() {
  const headerConfig = useContext(DashboardHeaderValueContext)

  if (!headerConfig) return null

  const { containerClassName, ...headerProps } = headerConfig

  return (
    <div className={containerClassName ?? "px-8 pt-8"}>
      <PostLoginHeader {...headerProps} />
    </div>
  )
}

export function usePostLoginHeader(config: DashboardHeaderConfig) {
  // Subscribe to the stable actions context only — this component will NOT
  // re-render when the header value changes, breaking the infinite update loop.
  const actions = useContext(DashboardHeaderActionsContext)
  const setHeaderConfig = actions?.setHeaderConfig
  const clearHeaderConfig = actions?.clearHeaderConfig
  const ownerId = useId()

  useEffect(() => {
    if (!setHeaderConfig) return
    setHeaderConfig(ownerId, config)
  }, [setHeaderConfig, ownerId, config])

  useEffect(() => {
    return () => clearHeaderConfig?.(ownerId)
  }, [clearHeaderConfig, ownerId])
}
